'use client';
import ButtonBase from '@mui/material/ButtonBase';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import type {MouseEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {AppIcon, FaVolumeOffIcon, FaVolumeUpIcon} from '../icon';

export interface SoundButtonProps {
  muted: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  size?: number;
  sx?: SxProps<Theme>;
}

// `position: relative` keeps it painted above the absolutely-positioned media
// element it sits beside, even without a positioned wrapper.
const Root = styled(ButtonBase, {shouldForwardProp: propName => propName !== 'oSize'})<{oSize: number}>(({theme, oSize}) => ({
  position: 'relative',
  flexShrink: 0,
  width: oSize,
  height: oSize,
  borderRadius: theme.radii.full,
  color: theme.palette.colors.icon.neutral.inverse,
  backgroundColor: theme.palette.colors.surface.darkOverlayStrong,
  '&:hover': {backgroundColor: theme.palette.colors.surface.darkOverlayStrongHover},
  '&:focus-visible': {outline: `2px solid ${theme.palette.colors.border.focus.onColor}`, outlineOffset: 2},
}));

export function SoundButton({muted, onClick, size = 38, sx}: SoundButtonProps) {
  const {t} = useTranslation();
  return (
    <Root type="button" oSize={size} onClick={onClick} aria-label={muted ? t('unmute') : t('mute')} aria-pressed={!muted} sx={sx}>
      <AppIcon icon={muted ? FaVolumeOffIcon : FaVolumeUpIcon} sx={{fontSize: '18px'}} aria-hidden />
    </Root>
  );
}

SoundButton.displayName = 'SoundButton';
